import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as mapsApi from '@/api/maps';
import type { MapFilters } from '@/api/maps';

const MAP_KEY = ['map'] as const;

export function useMapFamilies(filters?: MapFilters) {
  return useQuery({
    queryKey: [...MAP_KEY, 'families', filters],
    queryFn: () => mapsApi.getMapFamilies(filters),
    staleTime: 2 * 60 * 1000,
  });
}

export function usePendingReview(sectorId?: string) {
  return useQuery({
    queryKey: [...MAP_KEY, 'pending-review', sectorId],
    queryFn: () => mapsApi.getPendingReview(sectorId ? { sector_id: sectorId } : undefined),
    staleTime: 30 * 1000,
  });
}

export function useUpdateFamilyLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      familyId,
      latitude,
      longitude,
    }: {
      familyId: string;
      latitude: number;
      longitude: number;
    }) => mapsApi.updateFamilyLocation(familyId, latitude, longitude),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: MAP_KEY });
      // The family detail cache holds the coordinates that pre-seed the pin
      // on the "Corectează poziția" screen and the detail page. Without this
      // the saved location looks like it reverted (stale cache).
      void queryClient.invalidateQueries({
        queryKey: ['families', 'detail', variables.familyId],
      });
      void queryClient.invalidateQueries({ queryKey: ['families', 'list'] });
    },
  });
}

export function useMapMembers(filters?: MapFilters) {
  return useQuery({
    queryKey: [...MAP_KEY, 'members', filters],
    queryFn: () => mapsApi.getMapMembers(filters),
    staleTime: 2 * 60 * 1000,
  });
}

export function usePendingReviewMembers(sectorId?: string) {
  return useQuery({
    queryKey: [...MAP_KEY, 'pending-review-members', sectorId],
    queryFn: () => mapsApi.getPendingReviewMembers(sectorId ? { sector_id: sectorId } : undefined),
    staleTime: 30 * 1000,
  });
}

export function useUpdateMemberLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      latitude,
      longitude,
    }: {
      memberId: string;
      latitude: number;
      longitude: number;
    }) => mapsApi.updateMemberLocation(memberId, latitude, longitude),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: MAP_KEY });
      // Keep the member detail cache in sync so the corrected pin doesn't
      // revert to the stale coordinates when the screen is reopened.
      void queryClient.invalidateQueries({
        queryKey: ['members', 'detail', variables.memberId],
      });
      void queryClient.invalidateQueries({ queryKey: ['members', 'list'] });
    },
  });
}
